import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface SidebarContextType {
  isOpen: boolean;
  isCollapsed: boolean;
  isHoverExpanded: boolean;
  setHoverExpanded: (expanded: boolean) => void;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleCollapsed: () => void;
  collapse: () => void;
  expand: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('clicklaw.sidebarCollapsed') === '1';
    } catch {
      return false;
    }
  });
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const openSidebar = () => setIsOpen(true);
  const closeSidebar = () => setIsOpen(false);
  const toggleCollapsed = () => setIsCollapsed((v) => !v);
  const collapse = () => setIsCollapsed(true);
  const expand = () => setIsCollapsed(false);
  const setHoverExpanded = (expanded: boolean) => setIsHoverExpanded(expanded);

  useEffect(() => {
    if (!isCollapsed && isHoverExpanded) setIsHoverExpanded(false);
  }, [isCollapsed, isHoverExpanded]);

  useEffect(() => {
    try {
      window.localStorage.setItem('clicklaw.sidebarCollapsed', isCollapsed ? '1' : '0');
    } catch {
      // ignore storage failures (private mode, blocked storage, etc.)
    }
  }, [isCollapsed]);

  return (
    <SidebarContext.Provider
      value={{
        isOpen,
        isCollapsed,
        isHoverExpanded,
        setHoverExpanded,
        toggleSidebar,
        openSidebar,
        closeSidebar,
        toggleCollapsed,
        collapse,
        expand,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
